<template>
  <div class="tp">
    <section class="tp-selected">
      <div class="acq-label">{{ mode === 'assign' ? 'Zugeteilt' : 'Vorgemerkt' }} · {{ value.length }}</div>
      <div class="tp-chips">
        <span v-for="person in selectedPeople" :key="person.id" :class="{ 'tp-chip--new': person.isNew }" class="tp-chip">
          <span class="tp-chip__avatar">{{ person.initials }}</span>{{ person.name }}
          <span v-if="person.isNew" class="tag tag--sm tone-good">neu</span>
          <button v-if="!locked.includes(person.id)" :aria-label="`${person.name} entfernen`" class="tp-chip__btn" title="Entfernen" type="button"
                  @click="toggle(person.id)">
            <v-icon x-small>mdi-close</v-icon>
          </button>
          <v-icon v-else aria-label="Keine Berechtigung zum Zuordnen" title="Keine Berechtigung zum Zuordnen" x-small>mdi-lock-outline</v-icon>
        </span>
        <span v-for="person in removedPeople" :key="`r-${person.id}`" class="tp-chip tp-chip--removed">
          <span class="tp-chip__avatar">{{ person.initials }}</span><s>{{ person.name }}</s>
          <button :aria-label="`${person.name} wieder hinzufügen`" class="tp-chip__btn" title="Rückgängig" type="button" @click="toggle(person.id)">
            <v-icon x-small>mdi-undo</v-icon>
          </button>
        </span>
        <span v-if="!selectedPeople.length && !removedPeople.length" class="acq-tiny">
          {{ mode === 'assign' ? 'Noch keine Lehrkraft zugeteilt.' : 'Noch keine Lehrkraft vorgemerkt.' }} Unten aus den Vorschlägen wählen.
        </span>
      </div>
    </section>

    <section class="tp-filters">
      <div class="soft-inputs">
        <v-text-field v-model="term" clearable color="info" dense hide-details outlined placeholder="Andere Person per Name suchen (ohne Umkreis)"
                      prepend-inner-icon="mdi-magnify"/>
      </div>
      <div class="tp-filter-row">
        <div v-if="origin && !searching" aria-label="Umkreis" class="pills" role="group">
          <button v-for="km in radiusSteps" :key="km" :aria-pressed="String(radius === km)" :class="{ 'pill--active': radius === km }" class="pill"
                  type="button" @click="radius = km">
            {{ km }} km
          </button>
        </div>
        <segmented-control v-model="who" :items="whoItems" label="Wer"/>
        <button v-if="schoolHasSlots" :aria-pressed="String(onlyMatching)" :class="{ 'pill--active': onlyMatching }" class="pill" type="button"
                @click="onlyMatching = !onlyMatching">
          <v-icon class="pill__icon" x-small>{{ onlyMatching ? 'mdi-check' : 'mdi-calendar-check-outline' }}</v-icon>
          nur mit passender Verfügbarkeit
        </button>
      </div>
      <div class="acq-tiny tp-hint">
        <v-icon x-small>{{ searching ? 'mdi-magnify' : 'mdi-sort-descending' }}</v-icon>
        {{ hint }}
      </div>
    </section>

    <div v-if="loading || searchLoading" class="tp-list">
      <v-skeleton-loader v-for="n in 4" :key="n" class="tp-row" type="list-item-avatar-three-line"/>
    </div>
    <div v-else-if="!shown.length" class="tp-empty">
      <div class="tp-empty__icon"><v-icon color="grey">mdi-account-search-outline</v-icon></div>
      <div class="font-weight-medium">{{ emptyTitle }}</div>
      <div class="acq-tiny">{{ emptyText }}</div>
    </div>
    <div v-else class="tp-list">
      <div v-for="(row, index) in shown" :key="row.id" :aria-disabled="String(!!row.disabledReason)" :aria-pressed="String(isSelected(row.id))"
           :class="{ 'tp-row--selected': isSelected(row.id), 'tp-row--disabled': !!row.disabledReason }" class="tp-row" role="button" tabindex="0"
           @click="toggleRow(row)" @keydown.enter.prevent="toggleRow(row)" @keydown.space.prevent="toggleRow(row)">
        <div class="tp-avatar">
          {{ row.initials }}
          <span v-if="!searching && index < 3" class="tp-rank">{{ index + 1 }}</span>
        </div>
        <div class="tp-main">
          <div class="tp-name">
            <span class="text-truncate">{{ row.name }}</span>
            <span v-if="isSelected(row.id) && !initialIds.includes(row.id)" class="tag tag--sm tone-good">neu</span>
            <span v-else-if="initialIds.includes(row.id)" class="tag tag--sm tone-info">{{ mode === 'assign' ? 'zugeteilt' : 'vorgemerkt' }}</span>
          </div>
          <div class="tp-meta">
            <v-icon x-small>mdi-map-marker-outline</v-icon>
            <span>{{ row.city || 'Ort unbekannt' }}</span>
            <template v-if="row.distanceKm !== null">
              <span>·</span><strong>{{ formatKm(row.distanceKm) }}</strong><span>Luftlinie</span>
            </template>
          </div>
          <div v-if="subjects(row).length" class="tp-tags">
            <span v-for="subject in subjects(row)" :key="subject.name" class="tag tag--sm tp-subject">
              <span :style="{ background: subject.color || '#90A4AE' }" class="tp-subject__dot"/>{{ subject.name }}
            </span>
          </div>
          <div class="tp-tags">
            <span v-for="reason in row.reasons" :key="reason.text" :class="reason.tone" :title="reason.title" class="tag tag--sm">
              <v-icon class="tag__icon" x-small>{{ reason.icon }}</v-icon>{{ reason.text }}
            </span>
            <span v-if="docTag(row)" :class="docTag(row).tone" :title="docTag(row).title" class="tag tag--sm">
              <v-icon class="tag__icon" x-small>{{ docTag(row).icon }}</v-icon>{{ docTag(row).text }}
            </span>
            <span v-if="row.disabledReason" class="tag tag--sm tone-bad">
              <v-icon class="tag__icon" x-small>mdi-lock-outline</v-icon>{{ row.disabledReason }}
            </span>
          </div>
        </div>
        <div class="tp-side">
          <div :title="dayTitle(row)" aria-label="Verfügbarkeit Mo bis Fr" class="tp-days">
            <span v-for="(state, day) in row.days" :key="day" :class="`tp-day--${state}`" class="tp-day">{{ workDays[day] }}</span>
          </div>
          <div class="tp-stats">
            <span title="Zugeteilte Schulen"><v-icon x-small>mdi-school-outline</v-icon>{{ row.assignments }} {{ row.assignments === 1 ? 'Schule' : 'Schulen' }}</span>
            <span v-if="courseCount(row) !== null" title="Aktive Kurse">
              <v-icon x-small>mdi-account-group-outline</v-icon>{{ courseCount(row) }} {{ courseCount(row) === 1 ? 'Kurs' : 'Kurse' }}
            </span>
          </div>
        </div>
        <div class="tp-action">
          <span :class="{ 'tp-check--on': isSelected(row.id) }" class="tp-check">
            <v-icon small>{{ isSelected(row.id) ? 'mdi-check' : row.disabledReason ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
          </span>
          <a :href="$router.resolve(`/employees/${row.id}`).href" aria-label="Profil in neuem Tab öffnen" class="tp-open" target="_blank"
             title="Profil öffnen" @click.stop>
            <v-icon small>mdi-open-in-new</v-icon>
          </a>
        </div>
      </div>
      <div v-if="filtered.length > shown.length" class="text-center pt-2">
        <button class="cta cta--quiet" type="button" @click="limit += 20">Weitere {{ filtered.length - shown.length }} anzeigen</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import SegmentedControl from '@/components/common/modern/segmentedControl.vue';
import {Employee} from '@/interfaces/employee.interface';
import {Place} from '@/interfaces/place.interface';
import {LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {
  asObjectId, byScore, Candidate, findPeople, formatKm, MAX_RADIUS_KM, PickerMode, PickerWho, RADIUS_STEPS, Reason, toCandidate, WORK_DAYS,
} from './teacherPicker.helper';

type Person = { _id: Types.ObjectId; firstName?: string; lastName?: string };
type School = { name?: string; address?: Place; availabilities?: LabeledAvailabilityInterface[] };
type Details = { courses: number | null; missing: string[] | null };
type Chip = { id: string; name: string; initials: string; isNew: boolean };

const DEBOUNCE_MS = 300;
const DETAIL_COUNT = 10;

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase() || '?';
}

/**
 * Ranked teacher suggestions for a school (air-line distance, availability overlap, workload) plus a free name search.
 * Only edits the selection (v-model with employee ids), saving is done by the surrounding dialog.
 */
export default Vue.extend({
  components: {SegmentedControl},
  props: {
    mode: {type: String as () => PickerMode, required: true},
    institutionId: {type: Object as () => Types.ObjectId, required: true},
    /** Currently selected employee ids */
    value: {type: Array as () => string[], required: true},
    /** People selected when the dialog was opened */
    initial: {type: Array as () => Person[], required: true},
    /** Loads the suggestions whenever this becomes true */
    active: {type: Boolean, default: true},
    /** Optional names of preselected people that are not part of `initial` (id → name) */
    knownNames: {type: Object as () => Record<string, string>, default: () => ({})},
  },
  data: () => ({
    loading: false,
    searchLoading: false,
    school: null as School | null,
    origin: null as number[] | null,
    nearby: [] as Employee[],
    results: [] as Employee[],
    term: '' as string | null,
    timer: undefined as number | undefined,
    run: 0,
    searchRun: 0,
    radius: 20,
    who: 'teachers' as PickerWho,
    onlyMatching: false,
    limit: 20,
    details: {} as Record<string, Details>,
    names: {} as Record<string, string>,
  }),
  computed: {
    radiusSteps: () => RADIUS_STEPS,
    workDays: () => WORK_DAYS,
    whoItems: () => [
      {value: 'teachers', text: 'Lehrkräfte'},
      {value: 'applicants', text: 'Bewerber:innen'},
      {value: 'all', text: 'Alle'},
    ],
    searching(): boolean {
      return (this.term || '').trim().length >= 2;
    },
    initialIds(): string[] {
      return this.initial.map((person) => String(person._id));
    },
    /** Field that is written when (un)assigning */
    assignmentField(): EmployeeFields {
      return this.mode === 'assign' ? EmployeeFields.ASSIGNED_INSTITUTIONS : EmployeeFields.PLANNED_INSTITUTIONS;
    },
    /** Already assigned teachers the user may not remove */
    locked(): string[] {
      return this.initial.filter((person) => !this.canAssign(person._id)).map((person) => String(person._id));
    },
    schoolHasSlots(): boolean {
      return (this.school?.availabilities || []).length > 0;
    },
    candidates(): Candidate[] {
      const source = this.searching ? this.results : this.nearby;
      return source.map((employee) => {
        const row = toCandidate(employee, this.school, String(this.institutionId), this.mode);
        row.disabledReason = this.disabledReason(employee);
        return row;
      }).sort(byScore);
    },
    filtered(): Candidate[] {
      return this.candidates.filter((row) => (this.searching || row.distanceKm === null || row.distanceKm <= this.radius || this.isSelected(row.id))
        && (this.who === 'all' || (this.who === 'applicants') === !!row.employee.isApplicant)
        && (!this.onlyMatching || row.matchDays.length > 0));
    },
    shown(): Candidate[] {
      return this.filtered.slice(0, this.limit);
    },
    selectedPeople(): Chip[] {
      return this.value.map((id) => this.chip(id));
    },
    removedPeople(): Chip[] {
      return this.initialIds.filter((id) => !this.value.includes(id)).map((id) => this.chip(id));
    },
    hint(): string {
      if (this.searching) {
        return `Suchergebnisse für „${(this.term || '').trim()}“, unabhängig vom Umkreis`;
      }
      if (!this.origin) {
        return 'Die Schule hat keine Adresse mit Koordinaten, darum gibt es keine Vorschläge nach Entfernung. Bitte per Name suchen.';
      }
      const slots = this.schoolHasSlots ? 'passende Kurszeiten, ' : '';
      return `${this.filtered.length} im Umkreis von ${this.radius} km, sortiert nach ${slots}Entfernung, Wunschradius und Auslastung`;
    },
    emptyTitle(): string {
      return this.searching ? 'Niemand gefunden' : 'Keine Vorschläge für diese Filter';
    },
    emptyText(): string {
      if (this.searching) {
        return 'Anderen Namen versuchen oder bei „Wer“ auf „Alle“ stellen.';
      }
      return this.radius < MAX_RADIUS_KM ? 'Umkreis vergrößern, Filter lockern oder oben per Name suchen.' : 'Filter lockern oder oben per Name suchen.';
    },
    detailIds(): string {
      return this.shown.slice(0, DETAIL_COUNT).map((row) => row.id).filter((id) => !this.details[id]).join(',');
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.who = this.mode === 'assign' ? 'teachers' : 'all';
          this.term = '';
          this.onlyMatching = false;
          this.limit = 20;
          this.load();
        }
      },
    },
    term(value: string | null) {
      window.clearTimeout(this.timer);
      this.limit = 20;
      if (!value || value.trim().length < 2) {
        this.results = [];
        this.searchLoading = false;
        return;
      }
      this.searchLoading = true;
      this.timer = window.setTimeout(() => this.search(value.trim()), DEBOUNCE_MS);
    },
    radius() {
      this.limit = 20;
    },
    who() {
      this.limit = 20;
    },
    detailIds(ids: string) {
      if (ids) {
        this.loadDetails(ids.split(','));
      }
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.timer);
  },
  methods: {
    formatKm,
    isSelected(id: string): boolean {
      return this.value.includes(id);
    },
    toggle(id: string) {
      this.$emit('input', this.isSelected(id) ? this.value.filter((el) => el !== id) : [...this.value, id]);
    },
    toggleRow(row: Candidate) {
      if (row.disabledReason && !this.isSelected(row.id)) {
        return;
      }
      if (this.isSelected(row.id) && this.locked.includes(row.id)) {
        return;
      }
      this.$set(this.names, row.id, row.name);
      this.toggle(row.id);
    },
    chip(id: string): Chip {
      const person = this.initial.find((el) => String(el._id) === id);
      const name = person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unbekannt' : this.names[id] || (this.knownNames as Record<string, string>)[id] || 'Unbekannt';
      return {id, name, initials: initialsOf(name), isNew: !this.initialIds.includes(id)};
    },
    canAssign(id: Types.ObjectId): boolean {
      const entity = this.$$crudAccessManager.entity(CrudEntityTypes.EMPLOYEE, id);
      return entity.canRead(this.assignmentField) && entity.canUpdate(this.assignmentField);
    },
    disabledReason(employee: Employee): string | null {
      if (!this.canAssign(employee._id)) {
        return 'Keine Berechtigung zum Zuordnen';
      }
      if (this.mode !== 'assign') {
        return null;
      }
      if (employee.isApplicant) {
        return 'Bewerber:in, nur vormerken möglich';
      }
      if (!this.$$crudAccessManager.entity(CrudEntityTypes.EMPLOYEE, employee._id).canRead(EmployeeFields.IS_APPLICANT)) {
        return 'Kein Lesezugriff auf Bewerbungsstatus';
      }
      return null;
    },
    subjects(row: Candidate): Array<{ name: string; color?: string }> {
      return ((row.employee.subjects || []) as any[]).filter((el) => el?.name).slice(0, 4);
    },
    courseCount(row: Candidate): number | null {
      return this.details[row.id]?.courses ?? null;
    },
    docTag(row: Candidate): Reason | null {
      const missing = this.details[row.id]?.missing;
      if (!missing) {
        return null;
      }
      if (!missing.length) {
        return {text: 'Dokumente vollständig', icon: 'mdi-file-check-outline', tone: 'tone-good'};
      }
      return {text: missing.length === 1 ? '1 Dokument fehlt' : `${missing.length} Dokumente fehlen`, icon: 'mdi-file-alert-outline', tone: 'tone-warn',
        title: missing.join(', ')};
    },
    dayTitle(row: Candidate): string {
      if (!this.schoolHasSlots) {
        return 'Blau: Lehrkraft verfügbar (für Kurse). Die Schule hat keine Kurszeiten hinterlegt.';
      }
      return row.matchDays.length ? 'Grün: passt zu den Kurszeiten der Schule · Blau: verfügbar · Rahmen: Schule wünscht, Lehrkraft nicht verfügbar'
        : 'Keine Überschneidung mit den Kurszeiten der Schule (Rahmen = Tag der Schule)';
    },
    async loadSchool() {
      const id = this.institutionId;
      const canReadAddress = this.$$crudAccessManager.canAccessEntity(CrudEntityTypes.INSTITUTION, CrudActionTypes.READ, id, 'address');
      const school = await InstitutionerApi.findById(id, {fields: canReadAddress ? ['name', 'address', 'availabilities'] : ['name', 'availabilities']});
      this.school = school as School;
      this.$emit('school', school);
      const coordinates = school.address?.location?.coordinates;
      this.origin = coordinates && coordinates.length === 2 && coordinates[0] !== 0 && coordinates[1] !== 0 ? coordinates : null;
    },
    async load() {
      const run = ++this.run;
      this.loading = true;
      this.nearby = [];
      try {
        await this.loadSchool();
        if (this.origin) {
          const near = {lat: this.origin[1], lon: this.origin[0], maxDistance: MAX_RADIUS_KM * 1000};
          const people = await findPeople({near}, {skipPagination: true});
          if (run === this.run) {
            this.nearby = people;
          }
        }
      } catch (e) {
        this.$$showSnackbar('Beim Laden der Vorschläge ist ein Fehler aufgetreten', 'error');
      } finally {
        if (run === this.run) {
          this.loading = false;
        }
      }
    },
    async search(term: string) {
      const run = ++this.searchRun;
      try {
        const people = await findPeople({$search: term}, {limit: 10});
        if (run === this.searchRun) {
          this.results = people;
        }
      } catch (e) {
        if (run === this.searchRun) {
          this.results = [];
        }
      } finally {
        if (run === this.searchRun) {
          this.searchLoading = false;
        }
      }
    },
    /** Active courses and missing documents only for the first rows, so opening the dialog stays cheap */
    async loadDetails(ids: string[]) {
      ids.forEach((id) => this.$set(this.details, id, {courses: null, missing: null}));
      const objectIds = ids.map((id) => asObjectId(id));
      const docs = EmployeeHandlerAPI.find({
        filter: {_id: objectIds},
        fields: ['missingDocumentTypes'],
        populate: {missingDocumentTypes: {fields: ['name']}},
        skipPagination: true,
      } as any).then((resp) => resp.employees.forEach((employee) => {
        const missing = employee.missingDocumentTypes as any[] | undefined;
        if (Array.isArray(missing)) {
          this.$set(this.details, String(employee._id), {...this.details[String(employee._id)], missing: missing.map((el) => el?.name || 'Dokument')});
        }
      })).catch(() => undefined);
      const courses = objectIds.map((id) => CourseManagerAPI.find({
        filter: {teacher: [id], courseState: [CourseStates.ACTIVE], endsAfter: new Date()},
        limit: 1,
        fields: ['_id'],
      } as any).then((resp) => {
        this.$set(this.details, String(id), {...this.details[String(id)], courses: resp.maxPage || 0});
      }).catch(() => undefined));
      await Promise.all([docs, ...courses]);
    },
  },
});
</script>

<style scoped src="./teacherPicker.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
